// Vendor portal shell. Mirrors PortalLayout anatomy:
//   left column  → workspace logo or "Vendor Portal" kicker text;
//                  vendor's own name (their business identity) prominent
//                  below.
//   right column → theme toggle + Sign out.
//
// Nav swaps by viewport: desktop (≥768) keeps the top-of-page chip row;
// mobile (<768) hides the top nav and uses a fixed bottom tab bar
// (VendorBottomNav, defined at the foot of this file). The codebase's
// first bottom bar — co-located here so the nav-item array stays a
// single source of truth (any drift between the two surfaces would be
// the worst-case bug).
//
// V2a wires Jobs + History only. Settings and Team land in V2b — the
// nav renders only what exists.

import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { LogOut, Briefcase, History, Sun, Moon, Users, Settings } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { useIsMobile } from '@/lib/useIsMobile'
import { useVendorTheme } from '@/lib/vendorTheme'

// Bar height in CSS pixels. The math:
//   icon (16) + gap (3) + label (10) + vertical padding (8 + 8) ≈ 45
//   round to a comfortable 56 so the tap target clears Apple's 44pt
//   minimum with breathing room.
// Main padding-bottom = BAR_HEIGHT + 16 = 72; safe-area-inset stacks on
// top of that via env() so iPhone home-indicator devices clear too.
const BAR_HEIGHT_PX = 56

// Shared nav item type — used by BOTH the top-of-page chip row and the
// bottom tab bar. Lifting this out is the whole point: the two
// surfaces map the same array; a future Settings/Team toggle change
// can't cause drift.
interface NavItem { to: string; label: string; icon: typeof Briefcase }

// Narrow-viewport detector — mirrors PortalLayout's inline hook at the
// same 400px threshold (icons-only fallback for the desktop chip row;
// the bottom bar uses useIsMobile instead).
function useIsNarrow(): boolean {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 400px)').matches
  )
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(max-width: 400px)')
    const handler = (e: MediaQueryListEvent) => setNarrow(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return narrow
}

export default function VendorLayout() {
  const navigate = useNavigate()
  const { vendorUser, signOut } = useAuth()
  const isNarrow = useIsNarrow()
  const isMobile = useIsMobile()
  const { resolvedTheme, toggle: toggleTheme, setBrandColor } = useVendorTheme()

  // Single source of truth for nav destinations. Mapped by both the
  // top chip row (when !isMobile) and VendorBottomNav (when isMobile).
  // Admins get Team; techs don't. Order matters — bottom bar lays them
  // out left-to-right in this sequence.
  const navItems: NavItem[] = [
    { to: '/vendor/jobs',    label: 'Jobs',    icon: Briefcase },
    { to: '/vendor/history', label: 'History', icon: History },
    ...(vendorUser?.role === 'admin'
      ? [{ to: '/vendor/team', label: 'Team', icon: Users } as NavItem]
      : []),
    { to: '/vendor/settings', label: 'Settings', icon: Settings },
  ]

  // Vendor's own name (the subcontractor business). Pulled from the
  // vendors table directly — RLS scopes a vendor session to their own
  // row only (the vendor_read policy).
  const { data: vendor } = useQuery({
    queryKey: ['vendor-self', vendorUser?.vendor_id],
    enabled: !!vendorUser?.vendor_id,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase.from('vendors').select('id, name').eq('id', vendorUser!.vendor_id).maybeSingle()
      return data
    },
  })

  // Workspace branding via vendor_workspace view — same shape as
  // portal_workspace, vendor-scoped by the view's explicit WHERE clause.
  const { data: workspace } = useQuery({
    queryKey: ['vendor-workspace', vendorUser?.workspace_id],
    enabled: !!vendorUser?.workspace_id,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase.from('vendor_workspace').select('id, name, logo_url, brand_color').maybeSingle()
      return data as { id: string; name: string; logo_url: string | null; brand_color: string | null } | null
    },
  })

  useEffect(() => { setBrandColor(workspace?.brand_color ?? null) }, [workspace?.brand_color, setBrandColor])

  const handleSignOut = async () => {
    await signOut()
    navigate('/vendor/login', { replace: true })
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--canvas)', display: 'flex', flexDirection: 'column' }}>
      <header style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border-subtle)', padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {workspace?.logo_url ? (
            <img src={workspace.logo_url}
              alt={workspace?.name ?? 'Workspace logo'}
              style={{ display: 'block', height: 28, maxWidth: 120, width: 'auto', objectFit: 'contain', marginBottom: 4 }} />
          ) : (
            <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>Vendor Portal</p>
          )}
          <p style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink-primary)', margin: workspace?.logo_url ? 0 : '2px 0 0', letterSpacing: '-0.02em' }}>
            {vendor?.name ?? 'Loading…'}
          </p>
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <button type="button" onClick={toggleTheme}
            aria-label={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            title={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 8, background: 'transparent', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', cursor: 'pointer' }}>
            {resolvedTheme === 'dark'
              ? <Sun style={{ width: 14, height: 14 }} />
              : <Moon style={{ width: 14, height: 14 }} />}
          </button>
          <button type="button" onClick={handleSignOut}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            <LogOut style={{ width: 13, height: 13 }} />
            Sign out
          </button>
        </div>
      </header>

      {!isMobile && (
        <nav aria-label="Vendor navigation"
          style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border-subtle)', display: 'flex', gap: 4, padding: '8px 12px', overflowX: isNarrow ? 'visible' : 'auto', WebkitOverflowScrolling: 'touch', justifyContent: 'flex-start' }}>
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} end
              aria-label={label}
              title={isNarrow ? label : undefined}
              style={({ isActive }) => ({
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: isNarrow ? '8px 9px' : '7px 12px', borderRadius: 7,
                fontSize: 13, fontWeight: 600, textDecoration: 'none',
                background: isActive ? 'var(--accent-dim)' : 'transparent',
                color: isActive ? 'var(--accent-text)' : 'var(--ink-tertiary)',
                border: `1px solid ${isActive ? 'var(--accent-border)' : 'transparent'}`,
                flexShrink: 0,
              })}>
              <Icon style={{ width: isNarrow ? 16 : 13, height: isNarrow ? 16 : 13 }} />
              {!isNarrow && label}
            </NavLink>
          ))}
        </nav>
      )}

      <main style={{
        flex: 1,
        // Mobile: leave room for the fixed bottom bar. Safe-area inset
        // stacks on top of the base clearance via env() so iPhone home-
        // indicator devices clear too. Desktop padding unchanged —
        // pixel-equivalent to pre-bottom-bar.
        padding: isMobile
          ? `14px 16px calc(${BAR_HEIGHT_PX + 16}px + env(safe-area-inset-bottom))`
          : '14px 16px 24px',
        maxWidth: 800, width: '100%', margin: '0 auto', boxSizing: 'border-box',
      }}>
        <Outlet />
      </main>

      {isMobile && <VendorBottomNav items={navItems} />}
    </div>
  )
}

// ─── Bottom tab bar (mobile-only) ───────────────────────────────────────
//
// Fixed at bottom of viewport, full width. Tap targets sit ABOVE the
// home indicator via env(safe-area-inset-bottom); the bar's background
// color extends BEHIND it (the padding-bottom is on the bar, not the
// inner row). Color-only active state — no per-tab background pill,
// which would clutter a fixed bar.
//
// z-index 40: below the modal scrim convention (zIndex 50 across the
// vendor modals — Decline reason on VendorJobs, and the Delay / Return
// visit / Confirm complete trio on VendorJobDetail). Modals overlay
// the bar, taps on the bar pass through the scrim correctly.
//
// Items come from VendorLayout as a prop — single source of truth.
function VendorBottomNav({ items }: { items: NavItem[] }) {
  return (
    <nav aria-label="Vendor navigation"
      style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 40,
        background: 'var(--surface)',
        borderTop: '1px solid var(--border-subtle)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        display: 'flex',
      }}>
      {items.map(({ to, label, icon: Icon }) => (
        <NavLink key={to} to={to} end
          aria-label={label}
          style={({ isActive }) => ({
            flex: 1, height: BAR_HEIGHT_PX,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 3, textDecoration: 'none',
            color: isActive ? 'var(--accent-text)' : 'var(--ink-tertiary)',
          })}>
          {({ isActive }) => (
            <>
              <Icon style={{ width: 20, height: 20 }} />
              <span style={{ fontSize: 10, fontWeight: isActive ? 700 : 500, letterSpacing: '0.02em' }}>
                {label}
              </span>
            </>
          )}
        </NavLink>
      ))}
    </nav>
  )
}
